import Konva from "konva";

declare global {
  interface Window {
    Konva: typeof Konva;
  }
}

export const getAllNodes = (node: Konva.Node): Record<string, any> | null => {
  if (!node) return null;

  return {
    type: node.getClassName(),
    name: node.name(),
    id: node.id(),
    absolutePosition: node.absolutePosition?.(), // position on screen
    rect: node.getClientRect(),
    attrs: node.attrs, // Get attributes like position, size, fill, etc.
    children: node.hasChildren()
      ? [...node.children].map(getAllNodes) // Recursively get children
      : [],
  };
};

export function getContentsOfElementsLayer() {
  const elementsLayer = window.Konva.stages[0].find(".Elements")[0];
  const overlayLayer = window.Konva.stages[0].find(".Overlay")[0];
  if (!elementsLayer || !overlayLayer) {
    return [];
  }
  return [...elementsLayer.children.toArray(), ...overlayLayer.children.toArray()].map(getAllNodes);
}

/**
 * Example usage:
 * TestUtils.findKonvaNodeByAttribute(TestUtils.getAllNodes(Konva.stages[0]),'text','2')
 */
export const findKonvaNodeByAttribute = (
  node: Record<string, any>,
  attribute: string,
  value: string,
  parent?: Record<string, any>
): { node: Record<string, any>; parents: Record<string, any>[] }[] => {
  const matches: { node: Record<string, any>; parents: Record<string, any>[] }[] = [];

  const attr = node?.attrs?.[attribute];
  if (attr && (attr === value || (typeof attr === "string" && attr.includes(value)))) {
    matches.push({
      node,
      parents: parent ? [parent] : [],
    });
  }

  if (node?.children && node?.children.length > 0) {
    node.children.forEach((child: Record<string, any>) => {
      const childMatches = findKonvaNodeByAttribute(child, attribute, value, node);
      matches.push(
        ...childMatches.map((match) => ({
          node: match.node,
          parents: [...match.parents, node],
        }))
      );
    });
  }

  return matches;
};

export function findKonvaNodesByType(node: Record<string, any>, type: string): Record<string, any>[] {
  const matches: Record<string, any>[] = [];

  if (node?.type === type) {
    matches.push(node);
  }

  if (node?.children && node?.children.length > 0) {
    node.children.forEach((child: Record<string, any>) => {
      const childMatches = findKonvaNodesByType(child, type);
      matches.push(...childMatches);
    });
  }

  return matches;
}
